<template>
	<div
		class="l-modal"
		:class="classList"
		@keydown.capture="keyHandler"
		@focusin="focusHandler($event, true)"
		@focusout="focusHandler($event, false)"
	>
		<c-transition appear>
			<div
				class="l-modal__overlay"
				tabindex="-1"
			></div>
		</c-transition>

		<div
			class="l-modal__scrollable"
			:class="{
				'--resize': resizeObserver,
				'--content-end': contentEnd,
				'--account-destination': accountDestination,
			}"
		>
			<transition
				name="modal"
				appear
			>
				<div
					class="l-modal__dialog"
					:class="[{ '--dark': dark, '--content-end': contentEnd }]"
					role="dialog"
					aria-modal="true"
					:aria-labelledby="$slots.header ? 'l-modal-title' : undefined"
				>
					<div class="l-modal__nav">
						<slot name="inversis-header" />

						<span class="l-modal__close-button">
							<c-icon-button
								v-if="!modal"
								class="l-modal__icon-button"
								icon="@icons/close"
								size="l"
								tabindex="0"
								:title="$t('MODAL.CLOSE')"
								:aria-label="$t('MODAL.CLOSE')"
								data-testid="modal-close-button"
								@click="close"
							/>
						</span>
					</div>
					<div
						class="l-modal__document"
						:hiddenOverflow="hiddenOverflow"
						role="document"
					>
						<section
							class="l-modal__container"
							:class="{ '--is-lighthouse': true }"
						>
							<div
								class="l-modal__wrapper"
								:class="{
									'--no-padding-top': !modal || $slots['inversis-header'],
									'--max-content': maxContent,
									'--content-end': contentEnd,
								}"
							>
								<div
									v-if="$slots.icon"
									class="l-modal__icon"
								>
									<slot name="icon" />
								</div>

								<span
									class="a11y-hide"
									tabindex="-1"
								></span>

								<header
									v-if="$slots.header"
									class="l-modal__header"
									:class="{ '--content-end': contentEnd }"
									data-testid="modal-header"
								>
									<h2
										id="l-modal-title"
										class="l-modal__header-title"
										:class="{
											'--no-line-after-title': noLineAfterTitle,
											'--content-end': contentEnd,
										}"
										tabindex="-1"
									>
										<slot name="header" />
									</h2>
								</header>

								<div
									:hiddenOverflow="hiddenOverflow"
									class="l-modal__content text-m-book"
								>
									<slot />
								</div>

								<div
									v-if="$slots.subcontent"
									class="l-modal__sub-content text-m-book"
								>
									<slot name="subcontent" />
								</div>

								<div
									v-if="$slots.buttons"
									class="l-modal__buttons"
									:class="{ '--sticky': stickyButtons }"
									data-testid="modal-buttons"
								>
									<slot name="buttons" />
								</div>

								<div
									v-if="$slots.actions"
									class="l-modal__actions text-m-medium"
									data-testid="modal-actions"
								>
									<slot name="actions" />
								</div>
							</div>
						</section>
					</div>
				</div>
			</transition>
		</div>
	</div>
</template>

<script>
import CTransition from '@components/c-transition.vue';
import CIconButton from '@components/c-icon-button.vue';

export default {
	name: 'l-modal',

	components: {
		CTransition,
		CIconButton,
	},

	props: {
		fullscreen: { type: Boolean },
		almostFullscreen: { type: Boolean },
		modal: { type: Boolean },
		dark: { type: Boolean },
		enterKeyStrict: { type: Boolean },
		disableResizeObserver: { type: Boolean },
		noBorders: { type: Boolean },
		hiddenOverflow: { type: Boolean },
		noLineAfterTitle: { type: Boolean, default: false },
		maxContent: { type: Boolean, default: false },
		contentEnd: { type: Boolean, default: false },
		accountDestination: { type: Boolean, default: false },
		stickyButtons: { type: Boolean, default: false },
	},

	data() {
		return {
			resizeObserver: false,
		};
	},

	mounted() {
		const focusableSelectors =
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
		const firstElement = Array.from(this.$el.querySelectorAll(focusableSelectors)).find(
			(el) => !el.closest('[hidden]') && !el.closest('[aria-hidden="true"]')
		);

		if (firstElement) {
			firstElement.focus();
		}
	},

	computed: {
		...mapState(useAppStore, ['isHybridSky', 'isIOS']),

		classList({ fullscreen, almostFullscreen, noBorders }) {
			return {
				'--fullscreen': fullscreen,
				'--almost-fullscreen': almostFullscreen,
				'--no-borders': noBorders,
			};
		},
	},

	methods: {
		keyHandler($event) {
			const { key } = $event;
			const buttons = this.$el.querySelectorAll('.button');

			if (key === 'Escape' && !this.modal) {
				this.close();
			}

			if (key === 'Tab') {
				const focusableSelectors =
					'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
				const focusableElements = Array.from(
					this.$el.querySelectorAll(focusableSelectors)
				).filter((el) => !el.closest('[hidden]') && !el.closest('[aria-hidden="true"]'));

				if (!focusableElements.length) return;

				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if ($event.shiftKey && document.activeElement === firstElement) {
					$event.preventDefault();
					lastElement.focus();
				} else if (!$event.shiftKey && document.activeElement === lastElement) {
					$event.preventDefault();
					firstElement.focus();
				}
			}

			if (key === 'Enter' && buttons?.length) {
				const isActiveElementButton =
					document.activeElement.localName === 'button' ||
					document.activeElement.getAttribute('role') === 'button';

				const button = this.enterKeyStrict
					? document.activeElement.localName === 'button'
						? document.activeElement
						: undefined
					: isActiveElementButton
						? document.activeElement
						: buttons[buttons.length - 1];

				/* istanbul ignore else */
				if (!button?.disabled && button?.click) {
					$event.preventDefault();
					button.click();
				} else if (this.enterKeyStrict) {
					$event.preventDefault();
				}
			}
		},

		focusHandler($event, resize) {
			if (
				this.isHybridSky &&
				!this.isIOS &&
				!this.disableResizeObserver &&
				$event.target?.tagName === 'INPUT'
			)
				this.resizeObserver = resize;
		},

		close() {
			if (!this.modal) {
				this.$el.dispatchEvent(new Event('close', { bubbles: true }));
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.modal-enter-active {
	animation: modalEnter 250ms ease-out;
}

@keyframes modalEnter {
	from {
		transform: scale(1.05);
		opacity: 0;
	}
}

.l-modal {
	display: flex;
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	padding: var(--spacing-xl);
	justify-content: center;
	align-items: center;
	perspective: 800px;
	will-change: transform;
	z-index: var(--z-index-modal);
}

.l-modal__overlay {
	position: fixed;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	margin: 0;
	padding: 0;
	background: RGBA(var(--color-primary-dark-constant, var(--color-primary-dark)), 0.75);
}

.l-modal__scrollable {
	display: flex;
	position: absolute;
	width: 100%;
	height: 100%;
	padding: var(--spacing-l);
	align-items: center;
	justify-content: center;
	overflow-x: hidden;
	overflow-y: auto;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;

	&.--resize {
		align-items: baseline;
	}

	@media (max-width: 425px) {
		&.--content-end {
			top: auto;
			bottom: 0;
			height: var(--l-modal-xs-min-height, 372px);
			width: 100%;
			padding: 0px;
		}

		&.--account-destination {
			height: 600px;
		}
	}
}

.l-modal__header {
	display: flex;
	justify-content: center;
	margin-bottom: var(--spacing-l);
}

.l-modal__header-title {
	display: flex;
	position: relative;
	justify-content: center;
	padding-bottom: var(--spacing-unit);
	color: var(--l-modal-header-title-color, RGB(var(--color-text-primary-dark-contrast)));
	font-size: var(--l-modal-header-title-font-size, 24px);
	font-family: var(--font-family);
	font-weight: var(--font-weight-extrabold);

	@media (max-width: 425px) {
		&.--content-end {
			border-bottom: 0px;
		}
	}
}

.l-modal__header-title:not(.--no-line-after-title)::after {
	content: '';
	position: absolute;
	width: var(--cbnk-bar-width, 140px);
	max-width: var(--cbnk-bar-max-width, 140px);
	bottom: 0;
	border-bottom: var(--cbnk-bar-border-size, 2px) solid
		RGB(var(--cbnk-bar-border-color, var(--color-secondary)));
	border-radius: var(--border-radius-xl);
}

.l-modal__content {
	min-height: var(--l-modal-content-min-height, unset);
	overflow: var(--cbnk-content-overflow, unset);
}

.l-modal__content[hiddenOverflow='true'] {
	overflow: hidden;
}

@media (spanning: single-fold-vertical) {
	.l-modal__scrollable {
		width: calc(env(fold-right) - env(fold-width));
	}
}

.l-modal__dialog {
	color: RGB(var(--l-modal-dialog-color, var(--color-text-primary)));
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
	max-height: 100%;
	justify-self: center;
	border-radius: var(--border-radius-m);
	background: var(--l-modal-dialog-bg, RGB(var(--color-surface-light)));
	box-shadow: var(--l-modal-dialog-box-shadow, var(--elevation-medium));
	padding-bottom: 32px;
	overflow-y: auto;

	@media (max-width: 425px) {
		&.--content-end {
			border-bottom-left-radius: 0px;
			border-bottom-right-radius: 0px;
			height: 100%;
			padding-bottom: 0px;
		}
	}
}

.l-modal__dialog.--dark {
	background-color: RGB(var(--color-primary));
}

.l-modal__document {
	display: flex;
	width: 100%;
	height: 100%;
	flex-direction: column;
	overflow-y: scroll;
	scroll-padding-block: var(--spacing-s);
	padding-bottom: var(--spacing-xs);
}

.l-modal__document[hiddenOverflow='true'] {
	overflow-y: auto;
	overflow-x: hidden;
}

.l-modal__container {
	display: flex;
	width: 100%;
	flex-direction: column;
}

.l-modal__nav {
	display: flex;
	flex-direction: row;
	width: 100%;
	/* Ensure the close icon is at the end of the container */
	justify-content: flex-end;
	align-items: center;
	background: var(--l-modal-color-gradient-header, var(--no-gradient)) 100%;
}

.l-modal__close-button {
	margin: var(--spacing-s);
}

.l-modal__close-button .l-modal__icon-button {
	color: RGB(var(--l-modal-dialog-color));
}

.l-modal__wrapper {
	display: flex;
	width: 100%;
	height: 100%;
	max-width: var(--full-width, 474px);
	flex-grow: 1;
	flex-direction: column;
	margin: 0 auto;
	text-align: center;
	padding: var(
		--l-modal-wrapper-padding,
		calc(var(--spacing-unit) * 5) var(--spacing-xl) var(--spacing-s) var(--spacing-xl)
	);

	&.--no-padding-top {
		padding-top: 0;
	}

	&.--max-content {
		max-width: max-content;
	}

	@media (max-width: 425px) {
		&.--content-end {
			padding: 0 var(--spacing-xs) 0 var(--spacing-xs);
		}
	}
}

.l-modal__icon {
	color: RGB(var(--color-secondary));
	display: flex;
	justify-content: center;
	margin-bottom: var(--spacing-m);
	font-size: 40px;
}

.l-modal__sub-content {
	display: flex;
	width: 100%;
	margin: var(--spacing-xl) auto 0;
}

.l-modal__buttons {
	display: flex;
	width: 100%;
	justify-content: center;
	flex-wrap: wrap;
	margin: var(--modal-buttons-margin, var(--spacing-2xl) auto 0);
	gap: var(--spacing-l);
	flex: 1;

	@media (max-width: 425px) {
		justify-content: center;
	}
}

.l-modal__buttons.--sticky {
	position: sticky;
	bottom: 0;
	padding: var(--spacing-m) 0;
	background: var(--l-modal-dialog-bg, RGB(var(--color-surface-light)));
	z-index: var(--z-index-over-page);
}

.l-modal__dialog.--dark .l-modal__buttons.--sticky {
	background: RGB(var(--color-primary));
}

.l-modal__buttons :deep(> .button) {
	min-width: 155px;
	flex-grow: 1;
	flex-shrink: 0;
	flex-basis: 120px;
}

.l-modal__buttons :deep(> .button:first-child:last-child) {
	margin-left: 0;
	margin-right: 0;
}

.l-modal__actions {
	margin-top: var(--spacing-xl);
}

@media ($s-screen) {
	.l-modal__buttons {
		justify-content: center;
	}

	.l-modal__buttons :deep(> *) {
		max-width: 296px;
	}

	.l-modal {
		align-items: center;
	}

	.l-modal__dialog {
		width: 100%;
		max-width: var(--l-modal-dialog-max-width, 862px);
	}
}

.l-modal.--no-borders {
	.l-modal__wrapper {
		padding: 0;
	}

	.l-modal__dialog {
		padding-bottom: 0;
	}

	.l-modal__container {
		padding: 0 !important;
	}
}

.l-modal.--fullscreen {
	.l-modal__scrollable {
		padding: 0;
	}

	.l-modal__dialog {
		margin: 0;
		max-width: none;
		width: 100%;
		height: 100%;
		border-radius: 0;
	}
}

.l-modal.--almost-fullscreen {
	.l-modal__close-button .l-modal__icon-button {
		font-size: 44px;
	}

	.l-modal__container {
		padding: 48px 0px 0px;
	}

	.l-modal__container.--is-lighthouse {
		padding: 70px 0px 0px;
		height: 100%;
	}

	.l-modal__dialog {
		margin: 0 auto;
		max-width: none;
		width: 95%;
		height: 100%;
		border-radius: 0;
	}

	.l-modal__wrapper {
		max-width: 100%;
		max-height: 100%;
	}

	.l-modal__content {
		min-height: 100%;
	}

	@media ($xs-screen) {
		.l-modal__close-button .l-modal__icon-button {
			font-size: 36px;
		}

		.l-modal__scrollable {
			padding: 0px;
		}

		.l-modal__dialog {
			width: 100%;
			height: 100%;
		}

		.l-modal__wrapper {
			margin: 0 auto;
			max-width: 100%;
			max-height: 100%;
		}
	}
}
</style>
